import {
  type BillingEvent,
  type BillingEventDetail,
  type BillingEventsListQuerySchema,
  type Paginated,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type BillingEventsParams = z.input<typeof BillingEventsListQuerySchema>;

const EVENTS = '/admin/billing-events';

/**
 * The store channel's trail (P23b3a).
 *
 * ## Its own api, not endpoints bolted onto `paymentsApi`
 *
 * `paymentsApi` defines itself as the **direct** channel — orders a human
 * entered and decided — and these are deliveries a store sent. They invalidate
 * nothing of each other's, so sharing a cache would put two unrelated resources
 * behind one set of tags for no benefit. `subscriptionsApi` is already separate
 * from `paymentsApi` for the same reason, "rather than a billing super-api".
 *
 * ## It publishes no event and invalidates nothing
 *
 * Every other admin api announces its writes on `EVENT_BUS` (W5). This one has
 * no writes: the table is append-only from a webhook the browser cannot observe,
 * so there is nothing to announce and nothing a local mutation could invalidate.
 * What keeps it current is the same thing that keeps the verification and
 * violation queues current — it **polls** (W5's "live queues poll"). A tag
 * invalidation would only ever fire on our own writes, and we have none.
 *
 * The detail is untagged deliberately: it is read once when an operator opens a
 * payload, it can never change, and a tag would only invite an invalidation
 * nothing can legitimately trigger.
 */
export const billingEventsApi = createApi({
  reducerPath: 'billingEventsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['BillingEvents'],
  endpoints: (builder) => ({
    listBillingEvents: builder.query<Paginated<BillingEvent>, BillingEventsParams>({
      query: (params) => ({ url: EVENTS, method: 'GET', params }),
      providesTags: ['BillingEvents'],
    }),

    /**
     * The payload — `billing.manage`, and **audited server-side on every call**.
     *
     * That is why there is no prefetch and no eager load anywhere: each read
     * writes a row saying who looked. Fetching it to fill a cache "in case" would
     * put an operator's name against a disclosure they never asked for.
     */
    billingEvent: builder.query<BillingEventDetail, string>({
      query: (id) => ({ url: `${EVENTS}/${id}`, method: 'GET' }),
    }),
  }),
});

export const { useListBillingEventsQuery, useBillingEventQuery } = billingEventsApi;
